import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, downloadFile } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { Search, MessageCircle, RefreshCw, FileSpreadsheet, ArrowLeft, CheckCircle2, XCircle, Clock, Eye } from 'lucide-react';
import { fmtDateTime, fmtRelative } from '@/lib/dates';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { cn } from '@/lib/utils';

const STATUS_META = {
  success: { icon: CheckCircle2, color: 'text-emerald-700 bg-emerald-50' },
  failed: { icon: XCircle, color: 'text-red-700 bg-red-50' },
  pending: { icon: Clock, color: 'text-amber-700 bg-amber-50' },
  skipped: { icon: Clock, color: 'text-slate-600 bg-slate-100' },
};

export default function WhatsAppLogPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', event: 'all', search: '', start_date: '', end_date: '' });
  const [retrying, setRetrying] = useState({});
  const [viewing, setViewing] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.event !== 'all') params.set('event', filters.event);
      if (filters.search) params.set('search', filters.search);
      if (filters.start_date) params.set('start_date', filters.start_date);
      if (filters.end_date) params.set('end_date', filters.end_date);
      const [l, s] = await Promise.all([
        api.get(`/whatsapp/logs?${params.toString()}`),
        api.get('/whatsapp/stats'),
      ]);
      setLogs(l.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters.status, filters.event, filters.search, filters.start_date, filters.end_date]);

  const retry = async (id) => {
    setRetrying({ ...retrying, [id]: true });
    try {
      const { data } = await api.post(`/whatsapp/logs/${id}/retry`);
      if (data.ok) toast.success('Pesan berhasil dikirim ulang');
      else toast.error(`Gagal: ${data.reason}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    } finally {
      setRetrying({ ...retrying, [id]: false });
    }
  };

  const exportExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.event !== 'all') params.set('event', filters.event);
      if (filters.start_date) params.set('start_date', filters.start_date);
      if (filters.end_date) params.set('end_date', filters.end_date);
      await downloadFile(`/whatsapp/logs/export?${params.toString()}`, 'Log_WhatsApp_PROAKTIF.xlsx');
      toast.success('Log WhatsApp berhasil diunduh');
    } catch (e) {
      toast.error('Gagal export');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Log Pengiriman WhatsApp"
        subtitle="Riwayat seluruh pengiriman notifikasi WhatsApp dengan status & retry."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Pengaturan' }, { label: 'WhatsApp', to: '/pengaturan/whatsapp' }, { label: 'Log' }]}
        action={
          <>
            <Link to="/pengaturan/whatsapp"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Kembali</Button></Link>
            <Button onClick={exportExcel} disabled={downloading} data-testid="export-wa-logs" className="bg-[#DC2626] hover:bg-[#B91C1C]">
              <FileSpreadsheet className="w-4 h-4 mr-2" />Export Excel
            </Button>
          </>
        }
      />

      {/* Stats charts */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold">Statistik 6 Bulan Terakhir</CardTitle></CardHeader>
            <CardContent style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="bulan" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sukses" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="gagal" stackId="a" fill="#EF4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold">Ringkasan</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total Pesan</span><span className="font-extrabold tabular">{stats.total}</span></div>
              <div className="flex justify-between text-emerald-700"><span>Sukses</span><span className="font-extrabold tabular">{stats.success}</span></div>
              <div className="flex justify-between text-red-700"><span>Gagal</span><span className="font-extrabold tabular">{stats.failed}</span></div>
              <div className="flex justify-between text-amber-700"><span>Pending/Skip</span><span className="font-extrabold tabular">{stats.pending}</span></div>
              <div className="pt-2 border-t border-slate-200">
                <div className="flex justify-between"><span>Bulan Ini</span><span className="font-extrabold tabular">{stats.month_success}/{stats.month_total}</span></div>
                <div className="flex justify-between font-bold text-slate-800"><span>Success Rate</span><span className="tabular">{stats.success_rate}%</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4 grid md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="wa-log-search" placeholder="Cari target/nomor/pesan…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="wa-log-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="success">Sukses</SelectItem>
              <SelectItem value="failed">Gagal</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
          <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-premium overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>
        ) : logs.length === 0 ? (
          <EmptyState title="Belum ada log" icon={MessageCircle} description="Tidak ada pengiriman WhatsApp pada filter ini." />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="wa-logs-table">
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead className="w-40">Waktu</TableHead>
                  <TableHead className="w-40 hidden md:table-cell">Event</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead className="w-32 hidden lg:table-cell">Nomor</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-16 hidden md:table-cell">Try</TableHead>
                  <TableHead className="text-right w-28">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => {
                  const meta = STATUS_META[l.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  return (
                    <TableRow key={l.id} className="hover:bg-slate-50">
                      <TableCell className="text-xs font-mono text-slate-600">{fmtDateTime(l.created_at)}</TableCell>
                      <TableCell className="hidden md:table-cell"><span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{l.event}</span></TableCell>
                      <TableCell>
                        <div className="font-semibold text-sm">{l.target_name || '-'}</div>
                        <div className="text-xs text-slate-500 lg:hidden">{l.target_phone}</div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell font-mono text-xs">{l.target_phone}</TableCell>
                      <TableCell>
                        <span className={cn('inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase', meta.color)}>
                          <Icon className="w-3 h-3" /> {l.status}
                        </span>
                        {l.error && <div className="text-[10px] text-red-600 mt-0.5 truncate max-w-[180px]" title={l.error}>{l.error}</div>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-center font-mono text-xs">{l.attempts || 0}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="icon" variant="ghost" onClick={() => setViewing(l)} data-testid={`view-log-${l.id}`}><Eye className="w-4 h-4" /></Button>
                        {l.status !== 'success' && (
                          <Button size="icon" variant="ghost" disabled={retrying[l.id]} onClick={() => retry(l.id)} className="text-amber-600" data-testid={`retry-log-${l.id}`}>
                            <RefreshCw className={cn('w-4 h-4', retrying[l.id] && 'animate-spin')} />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Detail Log WhatsApp</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <Row label="Waktu" value={fmtDateTime(viewing.created_at)} />
              <Row label="Event" value={viewing.event} mono />
              <Row label="Target" value={`${viewing.target_name || '-'} (${viewing.target_role || '-'})`} />
              <Row label="Nomor" value={viewing.target_phone} mono />
              <Row label="Status" value={viewing.status?.toUpperCase()} />
              <Row label="Attempts" value={viewing.attempts || 0} />
              {viewing.error && <Row label="Error" value={viewing.error} />}
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Isi Pesan</div>
                <pre className="whitespace-pre-wrap p-3 rounded bg-slate-50 border border-slate-200 font-mono text-xs leading-relaxed max-h-72 overflow-y-auto">{viewing.message}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100">
      <div className="w-32 text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={cn('flex-1 text-slate-800 font-medium', mono && 'font-mono text-xs')}>{value}</div>
    </div>
  );
}
